// Runtime feature flags.
//
// Values come from `window.__APP_CONFIG` (public/app-config.js locally, generated
// at container startup in deployed environments) with the defaults declared in
// `config.js`. Read them through the helpers below rather than reaching into
// `globalConfig` directly, so the truthiness rules stay in one place.
import { globalConfig } from '@/configuration/config';

// app-config.js values arrive as strings ('true'/'') because they are generated
// from appsettings JSON, so a plain boolean check isn't enough.
export const isTruthy = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }

  return false;
};

/**
 * Milesight/LoRa thermostat scheduling (Thermostat Scheduling pages).
 * Off by default — the backend schedule endpoints are not available everywhere yet.
 */
export const isLoraSchedulesEnabled = () =>
  isTruthy(globalConfig.get().LORA_SCHEDULES);

/**
 * Shows Celsius equivalents when hovering Fahrenheit temperatures.
 * Off by default so the additional tooltip UI can be enabled independently.
 */
export const isTemperatureConversionTooltipEnabled = () =>
  isTruthy(globalConfig.get().TEMPERATURE_CONVERSION_TOOLTIP);

/**
 * The Admin Dashboard action on a Milesight/LoRa thermostat. Off by default:
 * it opens the external Pelican management site, which a LoRa device has no
 * presence on. Independent of LORA_SCHEDULES — Pelican tiles are unaffected
 * either way.
 */
export const isLoraAdminDashboardEnabled = () =>
  isTruthy(globalConfig.get().LORA_ADMIN_DASHBOARD);

/**
 * Turns off the five-minute `/version.json` poll that prompts for a refresh
 * after a deploy. The dev server has no version.json (Vite answers the request
 * with index.html), so on localhost every poll logs "Failed to check version".
 * Off by default: a missing or malformed value keeps the poll running in the
 * deployed environments, where it matters.
 */
export const isVersionCheckDisabled = () =>
  isTruthy(globalConfig.get().DISABLE_VERSION_CHECK);

/**
 * Turns off the Message Center unread-count poll (`GET /v1/MessageCenter/unread`
 * once at start-up and every minute after). Meant for localhost, where the
 * backend is often down and the poll fills the console with network errors.
 * Only the background poll is affected: refreshing the count after a user
 * action (Mark as Read, sending a message) still calls the endpoint.
 * Off by default for the same reason as the version check.
 */
export const isUnreadMessagesPollDisabled = () =>
  isTruthy(globalConfig.get().DISABLE_UNREAD_MESSAGES_POLL);

// The develop-ui-vue3 environment is used for recorded walkthroughs and live
// demos, where real customer site names must not appear on screen.
const DEMO_HOST_PATTERN = /develop-ui-vue3/i;

/** Pure predicate, split out from `areSiteNamesRedacted` so it can be tested. */
export const isDemoHostname = (hostname) =>
  DEMO_HOST_PATTERN.test(hostname ?? '');

/**
 * True when the app is served from the develop-ui-vue3 environment, in which
 * case site names are swapped for a neutral placeholder.
 *
 * `REDACT_SITE_NAMES` forces it on anywhere — it exists so the redaction can be
 * eyeballed on localhost, which the hostname rule deliberately never matches.
 * It can only turn redaction ON: on the demo host the hostname rule wins
 * regardless, so a stale 'false' in app-config.js can't unmask a live demo.
 */
export const areSiteNamesRedacted = () =>
  isTruthy(globalConfig.get().REDACT_SITE_NAMES) ||
  isDemoHostname(window.location?.hostname);

/** Placeholder shown in place of a real site name while redacted. */
export const REDACTED_SITE_NAME = 'Demo Site';

/**
 * Stable neutral label for one site within a list ('Site A', 'Site B', …).
 * Pickers have to stay navigable during the demo, so sites keep distinct
 * labels rather than collapsing onto the single placeholder above.
 */
export const redactedSiteLabel = (index) => {
  const letter = String.fromCharCode(65 + (index % 26));
  const cycle = Math.floor(index / 26);
  return `Site ${letter}${cycle > 0 ? cycle + 1 : ''}`;
};

/**
 * Returns the site name to display: the real one normally, the placeholder in
 * the demo environment.
 */
export const displaySiteName = (siteName) =>
  areSiteNamesRedacted() ? REDACTED_SITE_NAME : siteName;
